/**
 * LoginPage - PIN + DOB authentication.
 *
 * Visual layout matching `Primrose-Pymt-Portal-Homepage.html` and `Iris-Pymt-Portal-Homepage.html` 1:1.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { Alert } from '@tensaw/design-system/feedback';

import { ApiError } from '../../api/client';
import { useAuthenticate, useVerifyMagicLink } from '../../api/queries';
import { useAuth } from '../../auth/AuthContext';
import { sanitizeNext } from '../../auth/sanitizeNext';
import { dobIsoSchema } from '../../lib/dob';
import { LOGIN_THEME, CURRENT_BRAND } from '../../lib/loginTheme';
import { PrimroseLoginShell } from './shells/PrimroseLoginShell';
import { IrisLoginShell } from './shells/IrisLoginShell';
import { Dob3BoxInput } from './Dob3BoxInput';

const LoginShell = LOGIN_THEME === 'iris' ? IrisLoginShell : PrimroseLoginShell;

const SignInSchema = z.object({
  pin: z
    .string()
    .trim()
    .min(1, 'PIN is required')
    .max(32, 'PIN is too long'),
  // Full calendar validation (real month, real day for that month, leap
  // years, not in the future, not absurdly old) — see lib/dob.ts. The old
  // shape-only regex let month 99 / Feb 30 through to the backend, which
  // could only answer with the generic "doesn't match" copy.
  dob: dobIsoSchema,
});

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const auth = useAuth();
  const authenticate = useAuthenticate();
  const verifyMagicLink = useVerifyMagicLink();

  const [authMode, setAuthMode] = useState<'patient' | 'appointment'>('patient');
  const [pin, setPin] = useState('');
  const [dob, setDob] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  // Blur-time DOB message, kept separate from `validationError` so leaving
  // the DOB row does not wipe a PIN complaint from the last submit. Both
  // render through the single Alert below.
  const [dobError, setDobError] = useState<string | null>(null);

  useEffect(() => {
    if (auth.isAuthenticated) {
      navigate(sanitizeNext(params.get('next')), { replace: true });
    }
  }, [auth.isAuthenticated, navigate, params]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    setDobError(null);

    const parsed = SignInSchema.safeParse({ pin: pin.trim(), dob });
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      setValidationError(firstIssue?.message ?? 'Please check the form');
      return;
    }

    try {
      if (authMode === 'appointment') {
        const response = await verifyMagicLink.mutateAsync({
          token: parsed.data.pin,
          patient_dob: parsed.data.dob,
          client_ip: 'unknown',
          client_user_agent: 'unknown',
        });
        auth.refresh();
        // Per decision ledger #42: trust redirect_path over ?next=, since
        // the backend knows whether the token is patient- or
        // appointment-scope.
        navigate(response.redirect_path, { replace: true });
      } else {
        await authenticate.mutateAsync({
          patient_pin: parsed.data.pin,
          patient_dob: parsed.data.dob,
          client_ip: 'unknown',
          client_user_agent: 'unknown',
        });
        auth.refresh();
        navigate(sanitizeNext(params.get('next')), { replace: true });
      }
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.error(err);
    }
  };

  const isPending = authMode === 'appointment' ? verifyMagicLink.isPending : authenticate.isPending;

  // One error message, not two: the submit failure if there is one,
  // otherwise whatever the DOB field reported when the patient left it.
  const formError = validationError ?? dobError;

  const fieldClass = LOGIN_THEME === 'iris' ? 'ir-field' : 'pr-field';
  const labelClass = LOGIN_THEME === 'iris' ? 'ir-field-label' : 'pr-field-label';
  const hintClass = LOGIN_THEME === 'iris' ? 'ir-field-hint' : 'pr-field-hint';
  const inputClass = LOGIN_THEME === 'iris' ? 'ir-input ir-input-pin' : 'pr-input pr-input-pin';
  const btnClass = LOGIN_THEME === 'iris' ? 'ir-btn-primary' : 'pr-btn-primary';

  const toggleClass = LOGIN_THEME === 'iris' ? 'ir-auth-toggle' : 'pr-auth-toggle';
  const toggleBtnClass = LOGIN_THEME === 'iris' ? 'ir-auth-toggle-btn' : 'pr-auth-toggle-btn';
  const toggleIconClass = LOGIN_THEME === 'iris' ? 'ir-auth-toggle-icon' : 'pr-auth-toggle-icon';
  const toggleTitleClass = LOGIN_THEME === 'iris' ? 'ir-auth-toggle-title' : 'pr-auth-toggle-title';
  const toggleTextClass = LOGIN_THEME === 'iris' ? 'ir-auth-toggle-text' : 'pr-auth-toggle-text';

  return (
    <LoginShell>
      <div className={toggleClass} role="tablist" aria-label="Sign-in method">
        <button
          type="button"
          role="tab"
          aria-selected={authMode === 'patient'}
          className={`${toggleBtnClass}${authMode === 'patient' ? ' is-active' : ''}`}
          onClick={() => setAuthMode('patient')}
        >
          <span className={toggleIconClass}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
              <circle cx="10" cy="7" r="3" />
              <path d="M4 17c0-3 2.5-5 6-5s6 2 6 5" />
              <path d="M2.5 4h5M2.5 6.5h5" />
            </svg>
          </span>
          <span className={toggleTextClass}>
            <span className={toggleTitleClass}>Pay my balance from statement</span>
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={authMode === 'appointment'}
          className={`${toggleBtnClass}${authMode === 'appointment' ? ' is-active' : ''}`}
          onClick={() => setAuthMode('appointment')}
        >
          <span className={toggleIconClass}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
              <rect x="2.5" y="3.5" width="15" height="14" rx="2" />
              <path d="M2.5 8h15" />
              <path d="M6 2v3M14 2v3" />
            </svg>
          </span>
          <span className={toggleTextClass}>
            <span className={toggleTitleClass}>Pay for my upcoming visit</span>
          </span>
        </button>
      </div>

      <div className={LOGIN_THEME === 'iris' ? 'ir-explainer' : 'pr-explainer'}>
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
          <circle cx="10" cy="10" r="8" />
          <path d="M10 9v4M10 6.5v.4" />
        </svg>
        <span>To protect your privacy, please enter your individual PIN and date of birth. We’ll display your bill only when both details match your provider’s records.</span>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <div className={fieldClass}>
          <label htmlFor="pin" className={labelClass}>
            {authMode === 'appointment' ? 'Access PIN' : 'Statement PIN'}
          </label>
          <input
            id="pin"
            type="text"
            inputMode="text"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            placeholder="XXXX-XXXX-XXXX"
            className={inputClass}
            value={pin}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 12);
              const groups = raw.match(/.{1,4}/g);
              setPin(groups ? groups.join('-') : '');
            }}
          />
          <span className={hintClass}>
            The 12-character code printed on your statement or letter.
          </span>
        </div>

        <div className={fieldClass}>
          <label htmlFor="dob-m" className={labelClass}>Date of birth</label>
          <Dob3BoxInput
            theme={LOGIN_THEME}
            value={dob}
            onChange={setDob}
            onValidate={setDobError}
            error={Boolean(formError)}
          />
        </div>

        {formError && (
          <Alert variant="error" title="Check your details">
            {formError}
          </Alert>
        )}

        {authMode === 'appointment' && verifyMagicLink.isError && (
          <Alert variant="error" title="Sign-in failed">
            {mapMagicLinkError(verifyMagicLink.error)}
          </Alert>
        )}

        {authMode === 'patient' && authenticate.isError && (
          <Alert variant="error" title={authMutationErrorTitle(authenticate.error)}>
            {mapAuthError(authenticate.error)}
          </Alert>
        )}

        <button
          type="submit"
          className={btnClass}
          disabled={isPending}
        >
          {isPending ? 'Signing in...' : 'Continue'}
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 3l5 5-5 5" />
          </svg>
        </button>
      </form>

      <p className={LOGIN_THEME === 'iris' ? 'ir-help' : 'pr-help'}>
        Can&rsquo;t find your PIN? Call billing at <a href={`tel:${CURRENT_BRAND.billingPhoneTel}`}>{CURRENT_BRAND.billingPhoneDisplay}</a>.
      </p>
    </LoginShell>
  );
}

function authMutationErrorTitle(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.problem.error_code === 'PATIENT_PORTAL_PIN_INVALID') {
      return "We couldn't sign you in";
    }
    if (e.problem.error_code === 'PATIENT_PORTAL_LOGIN_RATE_LIMITED') {
      return 'Too many attempts';
    }
    if (e.problem.error_code === 'PATIENT_PORTAL_KEYSTORE_UNAVAILABLE') {
      return 'Service temporarily unavailable';
    }
  }
  return "We couldn't sign you in";
}

// Backend returns a single generic error code for all magic-link failure
// modes (anti-enumeration) — see MagicLinkDobForm for the same copy.
function mapMagicLinkError(e: unknown): string {
  if (e instanceof ApiError) {
    return e.problem.detail || 'Sign-in failed. Please check your Access PIN and date of birth.';
  }
  return 'Something went wrong. Please try again.';
}

function mapAuthError(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.problem.error_code) {
      case 'PATIENT_PORTAL_PIN_INVALID':
        return "PIN or date of birth doesn't match. Please try again.";
      case 'PATIENT_PORTAL_LOGIN_RATE_LIMITED':
        return 'Too many attempts. Please wait a few minutes and try again.';
      case 'PATIENT_PORTAL_KEYSTORE_UNAVAILABLE':
        return 'Service temporarily unavailable. Please try again in a moment.';
      default:
        return e.problem.detail || 'Something went wrong. Please try again.';
    }
  }
  return 'Something went wrong. Please try again.';
}
